import React, { Suspense, useEffect, useRef, useState } from "react";

import styles from "../../_styles/Choose.module.scss";
import { ISectionItemProps } from "../../_types/interfaces";
import useInViewport from "../../../../hooks/useInViewPort";
import ReactPlayer from "react-player";
import PlayerControllerWrapper from "../playerController/PlayerControllerWrapper";
import ChooseFooter from "../DesctopComponents/ChooseFooter";
import MobileCover from "../MobileComponents/MobileCover";
import { useDispatch } from "react-redux";
import {
  setCurrentItem,
  setCurrentVideoId,
  setIsAds,
  setLastVisitedId,
} from "../../../../store/slices/currentItem.slice";
import { setMutedState } from "../../../../store/slices/filters.slice";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../constants/routes";
import FiltersHeader from "../FiltersHeader/FiltersHeader";
import { useGetCurrentVideoQuery } from "../../../../store/api/filmAPI";
import { useTranslation } from "react-i18next";
import { languageHandler } from "../../../../utils/helpers";
import { AdSkip } from "../../../../components/shared/AdsSkip/AdsSkip";
import AdsBottomMenu from "../playerController/AdsBottomMenu";

const applyYoutubeMutedState = (youtubePlayer: any, shouldMute: boolean) => {
  if (!youtubePlayer) return;

  if (shouldMute) {
    youtubePlayer?.mute?.();
    return;
  }

  youtubePlayer?.unMute?.();
  youtubePlayer?.setVolume?.(100);
};

const DesctopPlayer = ({
  item,
  muted,
  filterErrorItem,
  skipAdsHandler,
  onTrailerWatched,
}: ISectionItemProps) => {
  const [url, setUrl] = useState("");
  const [player, setPlayer] = useState(null);
  const [time, setTime] = useState<number | number[]>(0);
  const [maxValue, setMaxValue] = useState<number | number[]>(0);
  const [displayVolume, setDisplayVolume] = useState<boolean>(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(true);
  const errorHandledRef = useRef(false);
  const playerWrapperRef = useRef<HTMLDivElement | null>(null);
  const { i18n } = useTranslation();
  const { inViewport, targetRef } = useInViewport(url);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data } = useGetCurrentVideoQuery(
    {
      queries: { language: languageHandler(i18n.language) },
      id: item.id,
    },
    {
      skip: item.commercial_url ? true : !inViewport,
    },
  );

  useEffect(() => {
    errorHandledRef.current = false;

    if (!item.commercial_url) {
      const trailer = data?.results.find((item: any) => item.type === "Trailer") || data?.results[0];
      setUrl(trailer ? trailer?.key : "");

      if (inViewport && data?.results && !trailer) {
        errorHandledRef.current = true;
        filterErrorItem(item.id);
      }
    } else {
      setUrl(item.commercial_url);
    }
  }, [data, filterErrorItem, inViewport, item]);

  const handlePlayerError = () => {
    if (errorHandledRef.current) return;

    errorHandledRef.current = true;
    filterErrorItem(item.id);
  };

  useEffect(() => {
    if (inViewport) {
      dispatch(setCurrentVideoId(item.id));
      dispatch(setCurrentItem(item));
      dispatch(setIsAds(!!item.commercial_url));
    }

    if (!inViewport) {
      setReady(false);
      setPlaying(true);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewport]);

  const clickHandler = () => {
    if (ready) {
      navigate(`${ROUTES.DETAILS}/${item.id}`);
      dispatch(setLastVisitedId(item.id as number));
    }
  };

  const togglePlayback = () => {
    const youtubePlayer = player as any;

    if (playing) {
      youtubePlayer?.pauseVideo?.();
      setPlaying(false);
      return;
    }

    youtubePlayer?.playVideo?.();
    setPlaying(true);
  };

  const toggleMuted = () => {
    const nextMuted = !muted;
    dispatch(setMutedState(nextMuted));
    applyYoutubeMutedState(player, nextMuted);

    if (!nextMuted) {
      (player as any)?.playVideo?.();
      setPlaying(true);
    }
  };

  useEffect(() => {
    applyYoutubeMutedState(player, !!muted);
  }, [player, muted]);

  useEffect(() => {
    const element = playerWrapperRef.current;

    if (!element || !inViewport || item.commercial_url) {
      setDisplayVolume(false);
      return;
    }

    let timerId: number | any = null;

    const showControls = () => {
      setDisplayVolume(true);
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        setDisplayVolume(false);
      }, 3000);
    };

    const handleMouseMove = () => {
      showControls();
    };

    const handleMouseLeave = (event: any) => {
      const relatedTarget = event.relatedTarget as HTMLElement | null;

      if (relatedTarget && (element.contains(relatedTarget) || relatedTarget.closest("[data-movie-overlay]"))) {
        return;
      }

      clearTimeout(timerId);
      setDisplayVolume(false);
    };

    element.addEventListener("mouseenter", showControls);
    element.addEventListener("mousemove", handleMouseMove);
    element.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      element.removeEventListener("mouseenter", showControls);
      element.removeEventListener("mousemove", handleMouseMove);
      element.removeEventListener("mouseleave", handleMouseLeave);
      clearTimeout(timerId);
    };
  }, [inViewport, item.commercial_url]);

  return (
    <div ref={targetRef} className={styles.itemWrapperDesctop}>
      <div ref={playerWrapperRef} className={styles.desctopPlayer}>
        <Suspense fallback={<div></div>}>
          <FiltersHeader />
        </Suspense>
        {inViewport && (
          <div id={"mobileCover"}>
            <MobileCover ready={ready} />
            {!item?.commercial_url && (
              <PlayerControllerWrapper
                time={time}
                maxValue={maxValue}
                player={player}
                setTime={setTime}
                displayVolume={displayVolume}
                muted={!!muted}
                onToggleMuted={toggleMuted}
              />
            )}
          </div>
        )}
        {item?.commercial_url && inViewport && <AdSkip onSkip={() => skipAdsHandler("down", item.id)} />}
        {inViewport && url ? (
          <ReactPlayer
            className={styles.desctopPlayerFrame}
            controls={false}
            width={"100%"}
            height={"100%"}
            playsinline={true}
            volume={100}
            progressInterval={500}
            onProgress={(value: any) => setTime(Math.round(value.playedSeconds))}
            playing={playing}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            loop={true}
            muted={muted}
            url={`https://www.youtube.com/watch?v=${url}`}
            onError={handlePlayerError}
            onReady={(e: any) => {
              const youtubePlayer = e["player"];
              setPlayer(youtubePlayer);
              youtubePlayer?.getIframe?.()?.setAttribute("tabindex", "-1");
              applyYoutubeMutedState(youtubePlayer, !!muted);
              const duration = youtubePlayer.getDuration();
              setMaxValue(duration);

              onTrailerWatched(item.title, `https://www.youtube.com/watch?v=${url}`);

              setTimeout(() => {
                setReady(true);
              }, 200);
            }}
          />
        ) : (
          <div style={{ height: "350px", width: "100%" }} />
        )}
        {inViewport && url && (
          <button
            type="button"
            tabIndex={-1}
            className={styles.desctopVideoToggle}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              togglePlayback();
            }}
            aria-label={playing ? "Pause video" : "Play video"}
          />
        )}
        <div className={styles.bottomMenu}>
          {!item?.commercial_url ? (
            <ChooseFooter item={item} inViewport={inViewport} currentItemId={item?.id} clickHandler={clickHandler} />
          ) : (
            <AdsBottomMenu />
          )}
        </div>
      </div>
    </div>
  );
};

export default DesctopPlayer;
